import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { PayableDocument } from '../../../models/ap-document/ap-document';
import { ApAdvancePayment } from '../../../models/ap-advance/ap-advance';
import { ApAdvanceProxyService } from '../../../proxies/ap-advance.proxy.service';

/** One posted advance amount earmarked to a PO that this document draws on (#4516). */
interface PoAdvanceLine {
  advanceNumber: string;
  purchaseOrderNumber: string;
  allocatedAmount: number;
}

/**
 * Reusable "Resumen de documento" (#4516): a payable base document with what already reduced it (payments,
 * withholding via a posted Constancia de Retención) and the notes that adjust it — Debit Notes (+) and
 * Credit Notes (−) — resolving to the Net a pagar. When the document draws on Purchase Orders it also lists
 * the posted Advances (Anticipos) earmarked to those POs, as context. Lets a user see an invoice's full
 * adjusted state at the point of decision without opening the database. Fed by the aggregation endpoints (#4514).
 */
@Component({
  selector: 'app-payable-document-summary',
  templateUrl: './payable-document-summary.component.html',
  styleUrl: './payable-document-summary.component.scss',
})
export class PayableDocumentSummaryComponent implements OnInit {
  private readonly advanceService = inject(ApAdvanceProxyService);

  @Input({ required: true }) payable!: PayableDocument;
  @Input() currencyLabel = '';
  /** Show the "advances against the PO" context when the base document draws on Purchase Orders. */
  @Input() showAdvances = true;

  /** Posted advances earmarked to the base document's PO(s) — the amounts already paid ahead against them. */
  readonly advances = signal<PoAdvanceLine[]>([]);
  readonly advancesTotal = computed(() => this.advances().reduce((sum, a) => sum + a.allocatedAmount, 0));

  ngOnInit(): void {
    if (!this.showAdvances) return;
    const poRefs = (this.payable.document.purchaseOrderKeys ?? []).map((k) => k.reference ?? '').filter((r): r is string => !!r);
    if (poRefs.length === 0) return;

    const poSet = new Set(poRefs);
    this.advanceService.getPostedByPurchaseOrders(poRefs).subscribe({
      next: (response) => this.advances.set(this.toLines(response.data ?? [], poSet)),
      error: () => this.advances.set([]),
    });
  }

  /** Flatten each advance to the allocations that match one of the document's POs (an advance may span POs). */
  private toLines(advances: ApAdvancePayment[], poSet: Set<string>): PoAdvanceLine[] {
    const lines: PoAdvanceLine[] = [];
    for (const advance of advances) {
      for (const alloc of advance.allocations ?? []) {
        if (poSet.has(alloc.purchaseOrderReference)) {
          lines.push({
            advanceNumber: advance.number ?? '',
            purchaseOrderNumber: alloc.purchaseOrderNumber,
            allocatedAmount: alloc.allocatedAmount,
          });
        }
      }
    }
    return lines;
  }

  noteAmount(balanceDue?: number | null, totalAmount?: number | null): number {
    return balanceDue ?? totalAmount ?? 0;
  }
}
